import { Dispatch } from 'redux';
import { HYDRATE } from 'next-redux-wrapper';
import { createSelector } from 'reselect';
import { ParsedUrlQuery } from 'querystring';

import { RootState } from '../store';
import {
    FilterTypes,
    FiltersValues,
    INITIALISE,
    UPDATE,
    RESET,
    SETENABLED,
    FilterActionTypes,
    FiltersState,
    ResetFiltersAction,
    InitialiseFiltersAction,
    UpdateFilterAction,
    SetEnabledFilterAction,
    FiltersSelectedValues,
    DropDownFilter
} from './filters.types';
import { filtersOptions } from './filtersOptions';
import {
    buildSelectBedsTitle,
    buildSelectPriceTitleForSale,
    buildSelectPriceTitleToRent
} from 'search/helpers';

// Actions
export const updateFilters = (
    filters: FiltersSelectedValues
): InitialiseFiltersAction => ({
    type: INITIALISE,
    filters
});

export const setEnabledFilters = (): SetEnabledFilterAction => ({
    type: SETENABLED
});

export const resetFilters = (
    filters?: FilterTypes[],
    query?: ParsedUrlQuery
): ResetFiltersAction => ({
    type: RESET,
    filters,
    query
});

export const updateFilter = <T extends FilterTypes>(
    filterType: T,
    selectedValue: FiltersState[T]['selectedValue']
): UpdateFilterAction<T> => ({
    type: UPDATE,
    filterType,
    selectedValue
});

export const updateAndEnableFilter = <T extends FilterTypes>(
    filterType: T,
    selectedValue: FiltersState[T]['selectedValue']
): ((dispatch: Dispatch) => Promise<void>) => {
    return async (dispatch) => {
        await dispatch(updateFilter(filterType, selectedValue));
        dispatch(setEnabledFilters());
    };
};

const enabledAndDisableFilters = (state: FiltersState) => {
    const auctionDisabled =
        !state.preOwned.selectedValue &&
        (state.retirementHome.selectedValue || state.newHomes.selectedValue);
    const incentivesDisabled =
        !state.newHomes.selectedValue &&
        (state.retirementHome.selectedValue || state.preOwned.selectedValue);
    const preOwnedDisabled =
        !state.auction.selectedValue &&
        !state.isSharedOwnership.selectedValue &&
        state.incentives.selectedValue;
    const newHomesDisabled =
        !state.incentives.selectedValue &&
        !state.isSharedOwnership.selectedValue &&
        state.auction.selectedValue;
    const retirementHomeDisabled =
        !state.isSharedOwnership.selectedValue &&
        state.incentives.selectedValue;

    return {
        auction: {
            ...state.auction,
            disabled: auctionDisabled,
            selectedValue: auctionDisabled ? false : state.auction.selectedValue
        },
        incentives: {
            ...state.incentives,
            disabled: incentivesDisabled,
            selectedValue: incentivesDisabled
                ? false
                : state.incentives.selectedValue
        },
        preOwned: {
            ...state.preOwned,
            disabled: preOwnedDisabled,
            selectedValue: preOwnedDisabled
                ? false
                : state.preOwned.selectedValue
        },
        newHomes: {
            ...state.newHomes,
            disabled: newHomesDisabled,
            selectedValue: newHomesDisabled
                ? false
                : state.newHomes.selectedValue
        },
        retirementHome: {
            ...state.retirementHome,
            disabled: retirementHomeDisabled,
            selectedValue: retirementHomeDisabled
                ? false
                : state.retirementHome.selectedValue
        }
    };
};

const getSideEffectStateUpdates = (
    state: FiltersState,
    filterType: FilterTypes
) => {
    switch (filterType) {
        case 'propertyType':
            return {
                propertySubTypes: {
                    ...state.propertySubTypes,
                    selectedValue: []
                }
            };

        case 'propertySubTypes':
            return {
                propertyType: {
                    ...state.propertyType,
                    selectedValue: 'houses'
                }
            };
        case 'q':
            return {
                locationChanged: {
                    selectedValue: 'true'
                }
            };

        default:
            break;
    }
};

const initialState: FiltersState = {
    ...filtersOptions
};

export const filters = (
    state = initialState,
    action: FilterActionTypes
): FiltersState => {
    switch (action.type) {
        case HYDRATE: {
            return {
                ...state,
                ...action.payload.filters
            };
        }
        case INITIALISE: {
            const { filters } = action;

            const updatedFilters = Object.entries(filters).reduce(
                (state, current) => {
                    const [key, value] = current;
                    const filterField = state[key as keyof FiltersState];

                    if (filterField) {
                        return {
                            ...state,
                            [key]: {
                                ...filterField,
                                selectedValue: value
                            }
                        };
                    }

                    return state;
                },
                state
            );

            return {
                ...state,
                ...updatedFilters,
                locationChanged: {
                    selectedValue: 'false'
                }
            };
        }
        case UPDATE: {
            const { selectedValue, filterType } = action;

            const otherUpdates = getSideEffectStateUpdates(state, filterType);

            return {
                ...state,
                ...otherUpdates,
                [filterType]: {
                    ...state[filterType],
                    selectedValue
                }
            };
        }
        case SETENABLED: {
            const enableDisable = enabledAndDisableFilters(state);

            return {
                ...state,
                ...enableDisable
            };
        }
        case RESET:
            const { filters, query } = action;
            const { duration, transport_type: transportType } = query || {};

            const isTravelTimeSearch = duration && transportType;

            if (filters) {
                const resetFilters = filters.reduce<Partial<FiltersState>>(
                    (acc, filter) => ({
                        ...acc,
                        [filter]: {
                            ...state[filter],
                            selectedValue: ''
                        }
                    }),
                    {}
                );

                return {
                    ...state,
                    ...resetFilters
                };
            }

            return {
                radius: {
                    ...state.radius,
                    selectedValue: ''
                },
                maxBedrooms: {
                    ...state.maxBedrooms,
                    selectedValue: ''
                },
                minBedrooms: {
                    ...state.minBedrooms,
                    selectedValue: ''
                },
                propertyType: {
                    ...state.propertyType,
                    selectedValue: ''
                },
                maxPrice: {
                    ...state.maxPrice,
                    selectedValue: ''
                },
                minPrice: {
                    ...state.minPrice,
                    selectedValue: ''
                },
                maxPriceWeekly: {
                    ...state.maxPriceWeekly,
                    selectedValue: ''
                },
                minPriceWeekly: {
                    ...state.minPriceWeekly,
                    selectedValue: ''
                },
                maxPriceMonthly: {
                    ...state.maxPriceMonthly,
                    selectedValue: ''
                },
                minPriceMonthly: {
                    ...state.minPriceMonthly,
                    selectedValue: ''
                },
                priceFrequency: {
                    ...state.priceFrequency,
                    selectedValue: ''
                },
                propertySubTypes: {
                    ...state.propertySubTypes,
                    selectedValue: []
                },
                propertyAdded: {
                    ...state.propertyAdded,
                    selectedValue: ''
                },
                availableFrom: {
                    ...state.availableFrom,
                    selectedValue: ''
                },
                chainFree: {
                    ...state.chainFree,
                    selectedValue: false
                },
                priceReduced: {
                    ...state.priceReduced,
                    selectedValue: false
                },
                includeSharedAccommodation: {
                    ...state.includeSharedAccommodation,
                    selectedValue: true
                },
                includeRented: {
                    ...state.includeRented,
                    selectedValue: false
                },
                underOfferOrSold: {
                    ...state.underOfferOrSold,
                    selectedValue: false
                },
                mustHaves: {
                    ...state.mustHaves,
                    selectedValue: []
                },
                furnishedState: {
                    ...state.furnishedState,
                    selectedValue: ''
                },
                billsIncluded: {
                    ...state.billsIncluded,
                    selectedValue: false
                },
                petsAllowed: {
                    ...state.petsAllowed,
                    selectedValue: false
                },
                rentalTerm: {
                    ...state.rentalTerm,
                    selectedValue: false
                },
                isSharedOwnership: {
                    ...state.isSharedOwnership,
                    selectedValue: true,
                    disabled: false
                },
                auction: {
                    ...state.auction,
                    selectedValue: true,
                    disabled: false
                },
                retirementHome: {
                    ...state.retirementHome,
                    selectedValue: false,
                    disabled: false
                },
                newHomes: {
                    ...state.newHomes,
                    selectedValue: false,
                    disabled: false
                },
                preOwned: {
                    ...state.preOwned,
                    selectedValue: false,
                    disabled: false
                },
                incentives: {
                    ...state.incentives,
                    selectedValue: false,
                    disabled: false
                },
                duration: {
                    ...state.duration,
                    selectedValue: isTravelTimeSearch
                        ? state.duration.options[0].value
                        : ''
                },
                transportType: {
                    ...state.transportType,
                    selectedValue: isTravelTimeSearch
                        ? state.transportType.options[0].value
                        : ''
                },
                keywords: {
                    ...state.keywords,
                    selectedValue: ''
                },
                q: {
                    ...state.q
                },
                identifier: {
                    ...state.identifier
                },
                locationText: {
                    ...state.locationText,
                    selectedValue: ''
                },
                locationChanged: {
                    ...state.locationChanged,
                    selectedValue: ''
                }
            };
        default:
            return {
                ...state
            };
    }
};

// Selectors
const selectFilters = (state: RootState): FiltersState => state.filters;

const selectRadius = (state: RootState) => selectFilters(state).radius || {};
const selectBedsMin = (state: RootState) =>
    selectFilters(state).minBedrooms || {};
const selectBedsMax = (state: RootState) =>
    selectFilters(state).maxBedrooms || {};
const selectPropertyType = (state: RootState) =>
    selectFilters(state).propertyType;
const selectPropertySubTypes = (state: RootState) =>
    selectFilters(state).propertySubTypes || {};
const selectPropertyAdded = (state: RootState) =>
    selectFilters(state).propertyAdded || {};
const selectAvailableFrom = (state: RootState) =>
    selectFilters(state).availableFrom || {};
const selectMinPrice = (state: RootState) =>
    selectFilters(state).minPrice || {};
const selectMaxPrice = (state: RootState) =>
    selectFilters(state).maxPrice || {};
const selectMinPriceWeekly = (state: RootState) =>
    selectFilters(state).minPriceWeekly || {};
const selectMaxPriceWeekly = (state: RootState) =>
    selectFilters(state).maxPriceWeekly || {};
const selectMinPriceMonthly = (state: RootState) =>
    selectFilters(state).minPriceMonthly || {};
const selectMaxPriceMonthly = (state: RootState) =>
    selectFilters(state).maxPriceMonthly || {};
const selectPriceFrequency = (state: RootState) =>
    selectFilters(state).priceFrequency || {};
const selectUnderOfferOrSold = (state: RootState) =>
    selectFilters(state).underOfferOrSold || {};
const selectChainFree = (state: RootState) =>
    selectFilters(state).chainFree || {};
const selectPriceReduced = (state: RootState) =>
    selectFilters(state).priceReduced || {};
const selectIncludeSharedAccommodation = (state: RootState) =>
    selectFilters(state).includeSharedAccommodation || {};
const selectIncludeRented = (state: RootState) =>
    selectFilters(state).includeRented || {};
const selectFurnishings = (state: RootState) =>
    selectFilters(state).furnishedState || {};
const selectShortLets = (state: RootState) =>
    selectFilters(state).rentalTerm || {};
const selectPetsAllowed = (state: RootState) =>
    selectFilters(state).petsAllowed || {};
const selectBillsIncluded = (state: RootState) =>
    selectFilters(state).billsIncluded || {};
const selectMustHaves = (state: RootState) =>
    selectFilters(state).mustHaves || {};
const selectDuration = (state: RootState) =>
    selectFilters(state).duration || {};
const selectTransportType = (state: RootState) =>
    selectFilters(state).transportType || {};
// Buying options
const selectIsSharedOwnership = (state: RootState) =>
    selectFilters(state).isSharedOwnership || {};
const selectAuction = (state: RootState) => selectFilters(state).auction || {};
const selectRetirementHome = (state: RootState) =>
    selectFilters(state).retirementHome || {};
const selectNewHomes = (state: RootState) =>
    selectFilters(state).newHomes || {};
const selectPreOwned = (state: RootState) =>
    selectFilters(state).preOwned || {};
const selectHelpToBuy = (state: RootState) =>
    selectFilters(state).incentives || {};
const selectKeywords = (state: RootState) =>
    selectFilters(state).keywords || {};
const selectLocation = (state: RootState) => selectFilters(state).q || {};
const selectLocationText = (state: RootState) =>
    selectFilters(state).locationText || {};
const selectIdentifier = (state: RootState) =>
    selectFilters(state).identifier || {};
const selectLocationChanged = (state: RootState) =>
    selectFilters(state).locationChanged || {};

// Base selectors
export const radiusSelector = createSelector(selectRadius, (radius) => radius);
export const bedsMinSelector = createSelector(
    selectBedsMin,
    (bedsMin) => bedsMin
);
export const bedsMaxSelector = createSelector(
    selectBedsMax,
    (bedsMax) => bedsMax
);
export const minPriceSelector = createSelector(
    selectMinPrice,
    (minPrice) => minPrice
);
export const maxPriceSelector = createSelector(
    selectMaxPrice,
    (maxPrice) => maxPrice
);
export const minPriceWeeklySelector = createSelector(
    selectMinPriceWeekly,
    (minPriceWeekly) => minPriceWeekly
);
export const maxPriceWeeklySelector = createSelector(
    selectMaxPriceWeekly,
    (maxPriceWeekly) => maxPriceWeekly
);
export const minPriceMonthlySelector = createSelector(
    selectMinPriceMonthly,
    (minPriceMonthly) => minPriceMonthly
);
export const maxPriceMonthlySelector = createSelector(
    selectMaxPriceMonthly,
    (maxPriceMonthly) => maxPriceMonthly
);
export const priceFrequencySelector = createSelector(
    selectPriceFrequency,
    (priceFrequency) => priceFrequency
);
export const propertyTypeSelector = createSelector(
    selectPropertyType,
    (propertyType) => propertyType
);
export const propertySubTypesSelector = createSelector(
    selectPropertySubTypes,
    (propertySubType) => propertySubType
);
export const propertyAddedSelector = createSelector(
    selectPropertyAdded,
    (propertyAdded) => propertyAdded
);
export const availableFromSelector = createSelector(
    selectAvailableFrom,
    (availableFrom) => availableFrom
);
export const underOfferOrSoldSelector = createSelector(
    selectUnderOfferOrSold,
    (underOfferOrSold) => underOfferOrSold
);
export const chainFreeSelector = createSelector(
    selectChainFree,
    (chainFree) => chainFree
);
export const priceReducedSelector = createSelector(
    selectPriceReduced,
    (priceReduced) => priceReduced
);
export const includeSharedAccommodationSelector = createSelector(
    selectIncludeSharedAccommodation,
    (includeSharedAccommodation) => includeSharedAccommodation
);
export const includeRentedSelector = createSelector(
    selectIncludeRented,
    (includeRented) => includeRented
);
export const shortLetsSelector = createSelector(
    selectShortLets,
    (rentalTerm) => rentalTerm
);
export const billsIncludedSelector = createSelector(
    selectBillsIncluded,
    (billsIncluded) => billsIncluded
);
export const petsAllowedSelector = createSelector(
    selectPetsAllowed,
    (petsAllowed) => petsAllowed
);
export const furnishingsSelector = createSelector(
    selectFurnishings,
    (furnishings) => furnishings
);
export const mustHavesSelector = createSelector(
    selectMustHaves,
    (mustHaves) => mustHaves
);
export const durationSelector = createSelector(
    selectDuration,
    (duration) => duration
);
export const transportTypeSelector = createSelector(
    selectTransportType,
    (transportType) => transportType
);
// Buying options
export const sharedOwnershipSelector = createSelector(
    selectIsSharedOwnership,
    (isSharedOwnership) => isSharedOwnership
);
export const auctionSelector = createSelector(
    selectAuction,
    (auction) => auction
);
export const retirementHomeSelector = createSelector(
    selectRetirementHome,
    (retirementHome) => retirementHome
);
export const newHomesSelector = createSelector(
    selectNewHomes,
    (newHomes) => newHomes
);
export const preOwnedSelector = createSelector(
    selectPreOwned,
    (preOwned) => preOwned
);
export const helpToBuySelector = createSelector(
    selectHelpToBuy,
    (helpToBuy) => helpToBuy
);
export const keywordsSelector = createSelector(
    selectKeywords,
    (keywords) => keywords
);
export const locationTextSelector = createSelector(
    selectLocationText,
    (locationText) => locationText
);
export const locationSelector = createSelector(
    selectLocation,
    (location) => location
);
export const identifierSelector = createSelector(
    selectIdentifier,
    (identifier) => identifier
);
export const locationChangedSelector = createSelector(
    selectLocationChanged,
    (locationChanged) => locationChanged
);

// Others
export const isTravelTimeSearchSelector = createSelector(
    [durationSelector, transportTypeSelector],
    (duration, transportType) => {
        const isTravelTimeSearch =
            duration.selectedValue && transportType.selectedValue;

        return !!isTravelTimeSearch;
    }
);

const getNewHomesValue = (newHomes: boolean, preOwned: boolean) => {
    if (newHomes) {
        return !preOwned ? 'only' : '';
    }
    return preOwned ? 'exclude' : '';
};

const getRetirementValue = (
    newHomes: boolean,
    preOwned: boolean,
    retirement: boolean
) => {
    if (!retirement) {
        return preOwned || newHomes ? 'false' : '';
    }
    return 'true';
};

export const filtersValuesSelector = createSelector(
    selectFilters,
    (filters) => {
        const filtersValues: FiltersValues = Object.entries(filters).reduce(
            (accumulator, current) => {
                const [key, value] = current;

                return {
                    ...accumulator,
                    [key]: value.selectedValue
                };
            },
            {}
        );

        const {
            radius,
            maxBedrooms: beds_max,
            minBedrooms: beds_min,
            propertyType,
            maxPrice: price_max,
            minPrice: price_min,
            maxPriceWeekly: price_max_weekly,
            minPriceWeekly: price_min_weekly,
            maxPriceMonthly: price_max_monthly,
            minPriceMonthly: price_min_monthly,
            priceFrequency,
            propertySubTypes,
            propertyAdded,
            availableFrom,
            underOfferOrSold,
            chainFree,
            priceReduced,
            includeSharedAccommodation,
            includeRented,
            furnishedState: furnishings,
            rentalTerm: shortLets,
            billsIncluded,
            petsAllowed,
            mustHaves,
            isSharedOwnership,
            auction,
            retirementHome,
            newHomes,
            preOwned,
            incentives,
            duration,
            transportType,
            keywords,
            q,
            identifier,
            locationText,
            locationChanged
        } = filtersValues;

        return {
            radius,
            beds: {
                beds_min,
                beds_max,
                buttonTitle: ''
            },
            propertyType,
            price: { price_max, price_min, buttonTitle: '' },
            priceWeekly: {
                price_max_weekly,
                price_min_weekly,
                buttonTitle: ''
            },
            priceMonthly: {
                price_max_monthly,
                price_min_monthly,
                buttonTitle: ''
            },
            priceFrequency,
            propertySubType: propertySubTypes,
            propertyAdded,
            availableFrom,
            underOfferOrSold,
            chainFree,
            priceReduced,
            includeShared: includeSharedAccommodation,
            includeLet: includeRented,
            furnishings,
            shortLets,
            billsIncluded,
            petsAllowed,
            features: mustHaves,
            isSharedOwnership: isSharedOwnership ? 'true' : '',
            auction: auction ? '' : 'false',
            retirementHome: getRetirementValue(
                Boolean(newHomes),
                Boolean(preOwned),
                Boolean(retirementHome)
            ),
            newHomes: getNewHomesValue(Boolean(newHomes), Boolean(preOwned)),
            helpToBuy: incentives ? 'true' : '',
            duration,
            transportType,
            keywords,
            location: {
                value: q,
                id: identifier
            },
            locationText,
            locationChanged
        };
    }
);

const translationsMap = new Map([
    ['studio', 'studio'],
    ['studios', 'studios'],
    ['bed', 'bed'],
    ['beds', 'beds'],
    ['min', 'min'],
    ['max', 'max'],
    ['default', 'Any beds']
]);

export const bedsTitleSelector = createSelector<
    RootState,
    DropDownFilter,
    DropDownFilter,
    string
>(bedsMinSelector, bedsMaxSelector, (bedsMinState, bedsMaxState) =>
    buildSelectBedsTitle(
        parseInt(bedsMinState.selectedValue, 10),
        parseInt(bedsMaxState.selectedValue, 10),
        translationsMap
    )
);

type PriceTitleSelectorResult = { forSale: string; toRent: string };
export const priceTitleSelector = createSelector<
    RootState,
    DropDownFilter,
    DropDownFilter,
    DropDownFilter,
    DropDownFilter,
    DropDownFilter,
    DropDownFilter,
    DropDownFilter,
    PriceTitleSelectorResult
>(
    minPriceSelector,
    maxPriceSelector,
    minPriceWeeklySelector,
    maxPriceWeeklySelector,
    minPriceMonthlySelector,
    maxPriceMonthlySelector,
    priceFrequencySelector,
    (
        minPriceState,
        maxPriceState,
        minPriceWeeklyState,
        maxPriceWeeklyState,
        minPriceMonthlyState,
        maxPriceMonthlyState,
        priceFrequencyState
    ) => ({
        forSale: buildSelectPriceTitleForSale(
            minPriceState.selectedValue,
            maxPriceState.selectedValue
        ),
        toRent: buildSelectPriceTitleToRent(
            minPriceWeeklyState.selectedValue,
            maxPriceWeeklyState.selectedValue,
            minPriceMonthlyState.selectedValue,
            maxPriceMonthlyState.selectedValue,
            priceFrequencyState.selectedValue
        )
    })
);

export type Filters = ReturnType<typeof filtersValuesSelector>;

export default filters;
